/**
 * Wire shapes of the composed Create route — the ONE definition shared by the
 * builder (spec synthesis), functions (proxy, receipt, admission) and the
 * portal (review step):
 *
 *  - {@link ComposedStepV1}: one admitted step of this creation's Create — a
 *    whole game composed as a generated template, a structural adaptation of
 *    a catalog template, or one rule the library lacked (its checked record)
 *    — with how it joins the game and the server's receipt for it.
 *  - {@link ComposedSpecSynthesisV1}: what spec synthesis returns — a proven,
 *    canonical patch against a template (its structure joining the generated
 *    rules), the generated modules' records, and the review's change list.
 *  - {@link ComposedProofReceiptV1}: the server's record that the exact game
 *    of a spec digest passed the admission proof; functions authenticate it.
 *  - {@link ComposedSpecAcknowledgementV1}: the creator's reviewed choice —
 *    template, patch, receipt, art direction, bound to the exact prompt /
 *    settings / design digests and revisions — sent with requestBuild.
 */
import { GENERATED_TEMPLATE_PREFIX, MAINTAINED_LIBRARY, listTemplates, validateGeneratedTemplateAsyncIn, validateGeneratedTemplateStructureIn, validateStructure, type ComposeLibraryV1,
  type TemplateOverridesV1, type TemplateStructureV1, type TemplateV1 } from '@djgg/gameplay-kit/compose';
import { COMPOSED_GENERATED_MODULES_MAX, composedLibraryV1, validateGeneratedComposeModulesV1, type GeneratedComposeModuleV1 } from './composedModules.js';
import { COMPOSE_PLAN_LIMITS, composePlanKeepsTemplateV1, parseComposeModuleNeedV1, type ComposeModuleNeedV1, type ComposePlanTemplateV1, type ComposePlanV1 } from '../composePlan.js';
import { canonicalDigest, canonicalJson, validateSha256 } from '@djgg/gameplay-kit';
import type { AcceptedCompositionV1, CompositionArtDirectionV1 } from './gameSpec.js';
import { COMPOSED_CHANGES_MAX, COMPOSED_CHANGE_LINE_MAX_LEN, COMPOSED_PROFILE, COMPOSED_PROOF_SEEDS, composedSelectionIdV1,
  composedTemplateDesignV1, composedTemplateKindV1, composedTemplateOfSelectionV1, validateComposedGameSpecV1, type ComposedGameSpecV1 } from './composedSpec.js';
import type { ComposedRunStageV1 } from '../types.js';
import { COMPOSITION_BACKGROUND_SLOT, compositionSettingsContractV1, type CompositionSettingsContractV1 } from './compositionPresentation.js';
import { compositionArtTextV1, compositionMediaSlotOffersV1, deriveCompositionArtDirectionV1, type CompositionMediaSlotOfferV1 } from './compositionSelection.js';
import { COMPOSITION_ART_SLOT_MAX_LEN, validateCompositionArtDirectionV1 } from './gameSpec.js';
import type { GameDesign } from '../design/types.js';
import type { GameSettings } from './types.js';
import type { MaintainedFitCandidateV1 } from '../maintainedGameFit.js';

/** Every composed CATALOG template as a recommendation candidate (a
 * generated template is private to its creation and never one): its disclosed
 * contract as a design, its fixed play modes and its sprite slots (the render
 * kinds). The same retrieval call ranks them beside the maintained profiles.
 * The background is NOT offered: a composed game's background line is its
 * patched `backgroundRole`, which spec synthesis authors against the
 * scenery-only check ({@link composedArtDirectionV1}). */
export function composedFitCandidatesV1(): MaintainedFitCandidateV1[] {
  return listTemplates().map(t => {
    const spec = { schemaVersion: 1, profile: COMPOSED_PROFILE, template: t.id, title: t.title.slice(0, 80), patch: {} } as const;
    return {
      templateId: composedSelectionIdV1(t.id),
      label: t.title,
      summary: t.pitch,
      design: composedTemplateDesignV1(MAINTAINED_LIBRARY, t.id),
      playModes: [...compositionSettingsContractV1(spec).playModes],
      mediaSlots: compositionMediaSlotOffersV1(spec).filter(offer => offer.slot !== COMPOSITION_BACKGROUND_SLOT),
    };
  });
}

export const COMPOSED_RATIONALE_MAX_LEN = 600;
/** A receipt admits for this long; the builder's solver gate re-proves anyway. */
export const COMPOSED_PROOF_RECEIPT_TTL_MS = 24 * 60 * 60 * 1000;

export interface ComposedProofSummaryV1 {
  seeds: number[];
  /** Ticks each seed's solver run took to complete the objective. */
  ticks: number[];
  /** Signature modules whose stubbing made the proof fail. */
  stubChecked: string[];
}

export interface ComposedSpecSynthesisV1 {
  schemaVersion: 1;
  /** Selection id `composed@1/<template id>`. */
  templateId: string;
  templateDigest: string;
  /** Canonical reviewed patch (never `/title`). */
  patch: TemplateOverridesV1;
  specDigest: string;
  /** Deterministic change list (`describeComposedPatchV1`). */
  changes: string[];
  /** The model's note to the creator on how the idea maps onto the template. */
  rationale: string;
  proof: ComposedProofSummaryV1;
  /** LLM calls spent (1 + repair rounds). */
  calls: number;
  /** The generated modules the patch's structure uses; absent when none. */
  modules?: GeneratedComposeModuleV1[];
  /** The generated template the patch is against (a game composed whole); absent for a catalog template. */
  generatedTemplate?: TemplateV1;
}

/** The prior game a composed REVISION starts from: a live version's
 * accepted composed@1 game. Its template, patch, generated modules and
 * generated template are the revision's starting library and patch. */
export interface ComposedRevisionBaseV1 {
  /** The live version revised. */
  version: number;
  /** That version's accepted composition digest. */
  authorityDigest: string;
  /** Its accepted composed spec, exactly. */
  spec: ComposedGameSpecV1;
}

/** One checked step of a composed Create that the server receipted for one creation,
 * in order: at most one first step — the prior game of a REVISION (its
 * `base`, server-built from the live version, no provider call), the whole
 * game composed as a generated TEMPLATE (library modules only; id
 * {@link composedGeneratedTemplateIdV1}) or a structural ADAPTATION of a
 * catalog template (library modules only) — then each generated RULE the
 * library lacked, which joins the generated template or the base exactly as
 * it joins a catalog one. A rule's or an adaptation's integration is what the
 * GAME owes for it: its ops, the modules the objective must need, and the
 * events its new modules must show in play. An assembly-bearing template and
 * its unfinished rule prefixes are preparation only; the last rule and final
 * synthesis must prove the complete game before build admission. */
export type ComposedStepV1 =
  | { kind: 'base'; base: ComposedRevisionBaseV1; receipt: ComposedStepReceiptV1 }
  | { kind: 'template'; template: TemplateV1; assembly?: ComposedAssemblyV1; receipt: ComposedStepReceiptV1 }
  | { kind: 'adaptation'; changes: string[]; integration: TemplateStructureV1; receipt: ComposedStepReceiptV1 }
  | { kind: 'rule'; need: ComposeModuleNeedV1; module: GeneratedComposeModuleV1; integration: TemplateStructureV1; receipt: ComposedStepReceiptV1 };

/** A step as the builder returns it, before functions receipt it. */
export type ComposedStepDraftV1 = ComposedStepV1 extends infer S ? S extends ComposedStepV1 ? Omit<S, 'receipt'> : never : never;

/** A checked scaffold awaiting these exact planned rules, in order. Its step
 * receipt authenticates preparation, never gameplay admission. The last rule
 * and final synthesis must prove the complete game before a build is accepted. */
export interface ComposedAssemblyV1 {
  rules: ComposeModuleNeedV1[];
}

export function parseComposedAssemblyV1(value: unknown): ComposedAssemblyV1 {
  const raw = record(value, ['rules'], 'composed assembly');
  if (!Array.isArray(raw.rules) || raw.rules.length < 1 || raw.rules.length > COMPOSED_GENERATED_MODULES_MAX) {
    throw new Error(`Composed assembly: expected 1..${COMPOSED_GENERATED_MODULES_MAX} planned rules`);
  }
  const ids = new Set(MAINTAINED_LIBRARY.modules.keys());
  const rules = raw.rules.map(value => {
    const need = parseComposeModuleNeedV1(value, ids);
    ids.add(need.id);
    return need;
  });
  return { rules };
}

/** Validate the assembled prefix against its receipted plan. Never let a
 * caller skip, substitute or append a rule and thereby close the proof barrier. */
export function composedAssemblyPendingRulesV1(steps: readonly ComposedStepDraftV1[]): ComposeModuleNeedV1[] {
  const first = steps[0];
  if (first?.kind !== 'template' || !first.assembly) return [];
  const rules = first.assembly.rules;
  const actual = steps.slice(1);
  for (const [index, step] of actual.entries()) {
    if (step.kind !== 'rule' || !rules[index] || canonicalJson(step.need) !== canonicalJson(rules[index])) {
      throw new Error('Composed assembly: steps must follow the exact planned rules in order');
    }
  }
  return rules.slice(actual.length);
}

export function requireComposedAssemblyCompleteV1(steps: readonly ComposedStepDraftV1[]): void {
  const pending = composedAssemblyPendingRulesV1(steps);
  if (pending.length) throw new Error(`Composed assembly is unfinished: ${pending.map(rule => rule.id).join(', ')}`);
}

/** The server's record that one step was checked for one creation: the
 * canonical digest of the exact step (HMAC under a functions-held key, like
 * the proof receipt). */
export interface ComposedStepReceiptV1 {
  schemaVersion: 1;
  creationId: string;
  stepDigest: string;
  issuedAt: number;
  mac: string;
}

/** Steps one Create may take: one first step (a revision's base, a
 * generated template or an adaptation) plus the plan's missing rules. */
export const COMPOSED_STEPS_MAX = 1 + COMPOSED_GENERATED_MODULES_MAX;

/** The generated modules a Create's steps carry, in order: the base game's
 * own, then each rule step's — every module the synthesized game carries. */
export function composedStepsModulesV1(steps: readonly ComposedStepDraftV1[]): GeneratedComposeModuleV1[] {
  return steps.flatMap(step => step.kind === 'rule' ? [step.module] : step.kind === 'base' ? step.base.spec.modules ?? [] : []);
}

/** The module ids a step adds to the library its successors name (a rule's
 * own id, a base game's generated modules). */
export function composedStepModuleIdsV1(step: ComposedStepDraftV1): string[] {
  return composedStepsModulesV1([step]).map(module => module.id);
}

/** The structure a step owes the game, in Create order: a rule's or an
 * adaptation's integration, and a base game's own patch structure (none for
 * a generated template, which IS the game's structure). */
export function composedStepStructureV1(step: ComposedStepDraftV1): TemplateStructureV1[] {
  if (step.kind === 'base') return step.base.spec.patch.structure ? [step.base.spec.patch.structure] : [];
  return step.kind === 'template' ? [] : [step.integration];
}

/** The library a Create's steps make (before any spec is accepted): the
 * maintained catalog, the generated template a template step composed (or
 * the base game carries) and every generated module the steps carry — the one
 * library the steps after them, synthesis and admission resolve the game
 * against. */
export function composedStepsLibraryV1(steps: readonly ComposedStepDraftV1[]): ComposeLibraryV1 {
  composedAssemblyPendingRulesV1(steps);
  return composedLibraryV1(composedStepsModulesV1(steps), composedStepsTemplateV1(steps));
}

/** The generated template a Create's template step composed, or the one its
 * base game is a patch of; else null. */
export function composedStepsTemplateV1(steps: readonly ComposedStepDraftV1[]): TemplateV1 | null {
  const step = steps.find(s => s.kind === 'template' || s.kind === 'base');
  return step?.kind === 'template' ? step.template : step?.kind === 'base' ? step.base.spec.generatedTemplate ?? null : null;
}

/** The template selection every later step and synthesis names: the base
 * game's, the creation's generated one once its template step ran, else the
 * plan's catalog template (null for a generate plan before its step). */
export function composedStepsTemplateIdV1(plan: Pick<ComposePlanV1, 'templateId'>, steps: readonly ComposedStepDraftV1[]): string | null {
  const first = steps.find(s => s.kind === 'template' || s.kind === 'base');
  if (first?.kind === 'base') return composedSelectionIdV1(first.base.spec.template);
  if (first?.kind === 'template') return composedSelectionIdV1(first.template.id);
  return plan.templateId;
}

/** The base step of a revision of `authority`, the accepted composition of
 * live version `version`. Only a composed@1 game has one (a maintained
 * profile's game has no template patch to revise). */
export function composedRevisionBaseV1(authority: Pick<AcceptedCompositionV1, 'spec' | 'digest'>, version: number): ComposedRevisionBaseV1 {
  if (authority.spec.profile !== COMPOSED_PROFILE) throw new Error('Only a composed game has a revision base');
  return parseComposedRevisionBaseV1({ version, authorityDigest: authority.digest, spec: structuredClone(authority.spec) });
}

/** One step a plan runs before synthesis (the receipted step's input). */
export type ComposedPlannedStepV1 =
  | { kind: 'base'; base: ComposedRevisionBaseV1 }
  | { kind: 'template'; included: string[] }
  | { kind: 'adaptation'; changes: string[] }
  | { kind: 'rule'; need: ComposeModuleNeedV1 };

/** How a revision's plan runs. `base`: the plan keeps the live game's
 * template and changes only its data (words, numbers, colours, kinds' names)
 * or adds generated rules onto it (a `template` or `extend` fit with no
 * structural changes) — the receipted base game is the first step. `fresh`:
 * anything else (an adaptation, a structural extend, another catalog
 * template, a generated game) runs the full Create route from the plan, and
 * still lands as a new version of the same creation. There is no refusal. */
export type ComposedRevisionRouteV1 = 'base' | 'fresh';

export function composedRevisionRouteV1(plan: ComposePlanV1, base: ComposedRevisionBaseV1): ComposedRevisionRouteV1 {
  return composePlanKeepsTemplateV1(plan, composedSelectionIdV1(base.spec.template)) ? 'base' : 'fresh';
}

/** The live template a REVISION's plan may keep (the plan context's `live`):
 * the base game's template — a catalog one or the creation's own generated
 * one — with the live game's sprite slots keyed back to the template's kinds
 * (the keys a plan's art uses). Null when there is no composed base. */
export function composedRevisionPlanTemplateV1(base: ComposedRevisionBaseV1 | null): ComposePlanTemplateV1 | null {
  if (!base) return null;
  return {
    templateId: composedSelectionIdV1(base.spec.template),
    mediaSlots: compositionMediaSlotOffersV1(base.spec).filter(offer => offer.slot !== COMPOSITION_BACKGROUND_SLOT)
      .map(({ slot, role }) => ({ slot: composedTemplateKindV1(base.spec.patch, slot), role })),
  };
}

/** The steps a plan runs, in Create order. A fresh Create: a generated
 * template or the adaptation first, then each missing rule. A REVISION
 * (`base` given) on its base route ({@link composedRevisionRouteV1}): the
 * receipted base first, then each missing rule; on its fresh route, exactly
 * a fresh Create's steps. */
export function composedPlannedStepsV1(plan: ComposePlanV1, base: ComposedRevisionBaseV1 | null): ComposedPlannedStepV1[] {
  const rules = plan.missing.map(need => ({ kind: 'rule' as const, need }));
  if (base && composedRevisionRouteV1(plan, base) === 'base') return [{ kind: 'base', base }, ...rules];
  switch (plan.fit) {
    case 'template': return [];
    case 'adapt': return [{ kind: 'adaptation', changes: plan.changes }];
    case 'extend': return [...(plan.changes.length ? [{ kind: 'adaptation' as const, changes: plan.changes }] : []), ...rules];
    case 'generate': return [{ kind: 'template', included: plan.included }, ...rules];
    // Owner decision (2026-10-21): when nothing fits, Create builds new
    // reusable parts instead of stopping, so "none" runs the generate route
    // from what the idea asks for. FOLLOW-UP: remove "none" from the plan
    // (COMPOSE_PLAN_FITS, the parser and the prompt) together with its paid
    // eval:compose-plan run; until then it maps here.
    case 'none': return [{ kind: 'template', included: plan.included.length ? plan.included : plan.notIncluded }, ...rules];
  }
}

/** The next stage of a composed run's stage machine — the plan, the next
 * planned step, then synthesis — or null once its synthesis is saved. */
export function composedNextStageV1(
  run: { plan?: ComposePlanV1; steps: readonly unknown[]; result?: unknown },
  base: ComposedRevisionBaseV1 | null,
): ComposedRunStageV1 | null {
  if (!run.plan) return 'plan';
  if (run.steps.length < composedPlannedStepsV1(run.plan, base).length) return `step-${run.steps.length}`;
  return run.result ? null : 'synthesis';
}

export interface ComposedProofReceiptV1 {
  schemaVersion: 1;
  templateId: string;
  templateDigest: string;
  specDigest: string;
  /** Digest of the spec's `{id, version}` module closure. */
  modulesDigest: string;
  proof: ComposedProofSummaryV1;
  issuedAt: number;
  /** HMAC-SHA256 over the canonical receipt without `mac` (functions-held key). */
  mac: string;
}

export interface ComposedSpecAcknowledgementV1 {
  schemaVersion: 1;
  kind: 'composed-spec';
  templateId: string;
  templateDigest: string;
  patch: TemplateOverridesV1;
  specDigest: string;
  receipt: ComposedProofReceiptV1;
  promptDigest: string;
  settingsDigest: string;
  designDigest: string;
  inputRevision: string;
  settingsRevision: number;
  designRevision: number;
  artDirection: CompositionArtDirectionV1;
  /** The generated modules of the reviewed game (the receipt pins their digests); absent when none. */
  modules?: GeneratedComposeModuleV1[];
  /** The generated template of a game composed whole (the receipt pins its digest); absent for a catalog template. */
  generatedTemplate?: TemplateV1;
}

/** The acknowledgement a composed build is requested with: a prepared run's
 * proven synthesis and receipt and its art direction, bound to the exact
 * prompt, settings and design the build runs against and to their saved
 * revisions. The wizard's Create and the server's composed revision both
 * submit it. */
export async function composedSpecAcknowledgementV1(input: {
  result: Pick<ComposedSynthesisResultV1, 'synthesis' | 'receipt'>;
  artDirection: CompositionArtDirectionV1;
  prompt: string;
  settings: GameSettings;
  design: GameDesign;
  revisions: { input: string; settings: number; design: number };
}): Promise<ComposedSpecAcknowledgementV1> {
  const { synthesis, receipt } = input.result;
  const [promptDigest, settingsDigest, designDigest] = await Promise.all([
    canonicalDigest(input.prompt), canonicalDigest(input.settings), canonicalDigest(input.design),
  ]);
  return {
    schemaVersion: 1,
    kind: 'composed-spec',
    templateId: synthesis.templateId,
    templateDigest: synthesis.templateDigest,
    patch: synthesis.patch,
    specDigest: synthesis.specDigest,
    receipt,
    artDirection: input.artDirection,
    ...(synthesis.modules ? { modules: synthesis.modules } : {}),
    ...(synthesis.generatedTemplate ? { generatedTemplate: synthesis.generatedTemplate } : {}),
    promptDigest,
    settingsDigest,
    designDigest,
    inputRevision: input.revisions.input,
    settingsRevision: input.revisions.settings,
    designRevision: input.revisions.design,
  };
}

/** What the synthesis callable returns to the review step. */
export interface ComposedSynthesisResultV1 {
  synthesis: ComposedSpecSynthesisV1;
  receipt: ComposedProofReceiptV1;
  /** The design the build applies: the template's disclosed contract over the
   * patched game's render kinds. */
  design: GameDesign;
  /** Generated-art slots of the patched game (render kinds + background). */
  mediaSlots: CompositionMediaSlotOfferV1[];
  /** The fixed settings the build applies (solo, the game's orientation). */
  settings: CompositionSettingsContractV1;
}

/** A composed game's background art line: its patched `backgroundRole` (the
 * background slot offer's role), which `deriveComposedGameV1` has already
 * checked scenery-only. The ONE source of a composed background's look —
 * admission requires the acknowledged line to equal it. */
export function composedBackgroundArtV1(backgroundRole: string): string {
  return compositionArtTextV1(backgroundRole, COMPOSITION_ART_SLOT_MAX_LEN);
}

/** The art direction a composed game is built with: the recommender's
 * per-slot look (keyed by the TEMPLATE's kinds) carried through the patch's
 * renames onto the patched game's sprite slots, the deterministic derivation
 * from the creator's own idea for any sprite slot it did not map, and the
 * game's own background line ({@link composedBackgroundArtV1}). */
export function composedArtDirectionV1(input: {
  title: string;
  /** The recommendation's mapping for this template, if it recommended it. */
  recommended: CompositionArtDirectionV1 | null;
  patch: TemplateOverridesV1;
  mediaSlots: readonly CompositionMediaSlotOfferV1[];
  design?: GameDesign | null;
}): CompositionArtDirectionV1 {
  const derived = deriveCompositionArtDirectionV1({ title: input.title, slots: input.mediaSlots, design: input.design ?? null });
  const slots = input.mediaSlots.map(({ slot }) => slot);
  return validateCompositionArtDirectionV1({
    style: input.recommended?.style ?? derived.style,
    slots: Object.fromEntries(input.mediaSlots.map(({ slot, role }) => {
      if (slot === COMPOSITION_BACKGROUND_SLOT) return [slot, composedBackgroundArtV1(role)];
      const mapped = input.recommended?.slots[composedTemplateKindV1(input.patch, slot)];
      return [slot, mapped ? compositionArtTextV1(mapped, COMPOSITION_ART_SLOT_MAX_LEN) : derived.slots[slot]!];
    })),
  }, slots);
}

/** The art direction a composed REVISION is built with: the base game's
 * look is kept wherever the revision leaves the game's subject alone. Its
 * style carries over; a sprite slot whose kind (keyed by template kind,
 * through either game's renames) existed in the base with the SAME role line
 * keeps the base's exact art line; a kind whose role line changed, or a new
 * kind, takes the plan's line for it when the plan maps one, else the
 * deterministic derivation; the background is the revised game's own line
 * ({@link composedBackgroundArtV1}). A revision therefore redraws exactly the
 * subjects it changed (an unchanged line keeps the slot's generation basis,
 * `compositionSlotBasisV1`), never the ones a plan merely restated. */
export function composedRevisionArtDirectionV1(input: {
  title: string;
  /** The base game: its accepted spec and art direction. */
  base: { spec: ComposedGameSpecV1; artDirection: CompositionArtDirectionV1 };
  /** The revision plan's mapping (by template kinds), if it mapped one. */
  recommended: CompositionArtDirectionV1 | null;
  patch: TemplateOverridesV1;
  mediaSlots: readonly CompositionMediaSlotOfferV1[];
  design?: GameDesign | null;
}): CompositionArtDirectionV1 {
  const fresh = composedArtDirectionV1({ title: input.title, recommended: input.recommended, patch: input.patch,
    mediaSlots: input.mediaSlots, design: input.design ?? null });
  const prior = new Map(compositionMediaSlotOffersV1(input.base.spec)
    .filter(({ slot }) => slot !== COMPOSITION_BACKGROUND_SLOT)
    .map(({ slot, role }) => [composedTemplateKindV1(input.base.spec.patch, slot), { role, line: input.base.artDirection.slots[slot]! }]));
  return validateCompositionArtDirectionV1({
    style: input.base.artDirection.style,
    slots: Object.fromEntries(input.mediaSlots.map(({ slot, role }) => {
      const kept = slot === COMPOSITION_BACKGROUND_SLOT ? undefined : prior.get(composedTemplateKindV1(input.patch, slot));
      return [slot, kept && kept.role === role ? kept.line : fresh.slots[slot]!];
    })),
  }, input.mediaSlots.map(({ slot }) => slot));
}

function record(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Reflect.ownKeys(value).length !== keys.length
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) {
    throw new Error(`Invalid ${label}`);
  }
  return value as Record<string, unknown>;
}

/** The template id a selection names for a game that carries
 * `generatedTemplate` (a spec carrier, or a Create's steps through
 * {@link composedStepsTemplateV1}): that generated template, and only it,
 * when there is one; else a catalog template. Null when it names neither. */
export function composedSelectedTemplateV1(selectionId: string, generatedTemplate: TemplateV1 | null): string | null {
  if (generatedTemplate) return selectionId === composedSelectionIdV1(generatedTemplate.id) ? generatedTemplate.id : null;
  return composedTemplateOfSelectionV1(selectionId, MAINTAINED_LIBRARY);
}

function selectionId(value: unknown, generatedTemplate: TemplateV1 | null): string {
  if (typeof value !== 'string' || !composedSelectedTemplateV1(value, generatedTemplate)) throw new Error('Unknown composed template');
  return value;
}

/** A carrier's generated template, shape only: it is validated where the
 * game is derived (`composedCarriedLibraryV1`, or against the steps that
 * composed it). */
function carriedTemplate(raw: Record<string, unknown>): TemplateV1 | null {
  if (!Object.hasOwn(raw, 'generatedTemplate')) return null;
  const t = raw.generatedTemplate;
  if (!t || typeof t !== 'object' || Array.isArray(t) || typeof (t as { id?: unknown }).id !== 'string'
    || !(t as { id: string }).id.startsWith(GENERATED_TEMPLATE_PREFIX)) throw new Error('Invalid composed generated template');
  return structuredClone(t) as TemplateV1;
}

function lines(value: unknown, max: number, maxLen: number, label: string): string[] {
  if (!Array.isArray(value) || value.length > max
    || value.some(v => typeof v !== 'string' || !v.trim() || v.length > maxLen)) throw new Error(`Invalid ${label}`);
  return [...value as string[]];
}

export function parseComposedProofSummaryV1(value: unknown): ComposedProofSummaryV1 {
  const raw = record(value, ['seeds', 'ticks', 'stubChecked'], 'composed proof summary');
  const ints = (v: unknown) => Array.isArray(v) && v.every(n => Number.isSafeInteger(n) && (n as number) >= 0);
  if (!ints(raw.seeds) || !ints(raw.ticks) || (raw.seeds as number[]).length !== (raw.ticks as number[]).length
    || JSON.stringify(raw.seeds) !== JSON.stringify(COMPOSED_PROOF_SEEDS)) throw new Error('Invalid composed proof summary');
  return { seeds: [...raw.seeds as number[]], ticks: [...raw.ticks as number[]],
    stubChecked: lines(raw.stubChecked, 32, 64, 'composed proof stub checks') };
}

/** Closed parser of a synthesis result (builder reply, functions wire). The
 * patch is NOT trusted here: admission re-derives the game from it. */
export function parseComposedSpecSynthesisV1(value: unknown): ComposedSpecSynthesisV1 {
  const carries = (key: string) => !!value && typeof value === 'object' && Object.hasOwn(value, key);
  const raw = record(value, ['schemaVersion', 'templateId', 'templateDigest', 'patch', 'specDigest', 'changes', 'rationale', 'proof', 'calls',
    ...(carries('modules') ? ['modules'] : []), ...(carries('generatedTemplate') ? ['generatedTemplate'] : [])], 'composed spec synthesis');
  if (raw.schemaVersion !== 1 || !raw.patch || typeof raw.patch !== 'object' || Array.isArray(raw.patch)
    || typeof raw.rationale !== 'string' || !raw.rationale.trim() || raw.rationale.length > COMPOSED_RATIONALE_MAX_LEN
    || !Number.isSafeInteger(raw.calls) || (raw.calls as number) < 1 || (raw.calls as number) > 3) {
    throw new Error('Invalid composed spec synthesis');
  }
  validateSha256(raw.templateDigest, 'composed template');
  validateSha256(raw.specDigest, 'composed spec');
  const generatedTemplate = carriedTemplate(raw);
  return {
    schemaVersion: 1, templateId: selectionId(raw.templateId, generatedTemplate), templateDigest: raw.templateDigest as string,
    patch: structuredClone(raw.patch) as TemplateOverridesV1, specDigest: raw.specDigest as string,
    changes: lines(raw.changes, COMPOSED_CHANGES_MAX, COMPOSED_CHANGE_LINE_MAX_LEN, 'composed change list'), rationale: raw.rationale,
    proof: parseComposedProofSummaryV1(raw.proof), calls: raw.calls as number,
    ...(carries('modules') ? { modules: validateGeneratedComposeModulesV1(raw.modules) } : {}),
    ...(generatedTemplate ? { generatedTemplate } : {}),
  };
}

export function parseComposedStepReceiptV1(value: unknown): ComposedStepReceiptV1 {
  const raw = record(value, ['schemaVersion', 'creationId', 'stepDigest', 'issuedAt', 'mac'], 'composed step receipt');
  if (raw.schemaVersion !== 1 || typeof raw.creationId !== 'string' || !raw.creationId || raw.creationId.length > 128
    || !Number.isSafeInteger(raw.issuedAt) || (raw.issuedAt as number) < 1) throw new Error('Invalid composed step receipt');
  for (const key of ['stepDigest', 'mac'] as const) validateSha256(raw[key], `composed step receipt ${key}`);
  return { schemaVersion: 1, creationId: raw.creationId, stepDigest: raw.stepDigest as string, issuedAt: raw.issuedAt as number, mac: raw.mac as string };
}

/** Closed parser of one step before it is receipted (the builder's reply).
 * Its ops may name `moduleIds` (the library plus the rules before it) and,
 * for a rule, the rule itself. A generated template's verb proofs execute
 * here, yielding to the event loop as they run. */
export async function parseComposedStepDraftV1(value: unknown, moduleIds: ReadonlySet<string>): Promise<ComposedStepDraftV1> {
  const kind = value && typeof value === 'object' ? (value as { kind?: unknown }).kind : undefined;
  if (kind === 'base') {
    // A revision's first step: the prior game, exactly as accepted.
    const raw = record(value, ['kind', 'base'], 'composed revision base step');
    return { kind, base: parseComposedRevisionBaseV1(raw.base) };
  }
  if (kind === 'template') {
    // The scaffold uses maintained modules; its exact missing dependencies,
    // when present, must be assembled before any gameplay admission.
    const raw = record(value, ['kind', 'template', ...(Object.hasOwn(value as object, 'assembly') ? ['assembly'] : [])], 'composed template');
    const assembly = Object.hasOwn(raw, 'assembly') ? parseComposedAssemblyV1(raw.assembly) : undefined;
    const checked = assembly ? validateGeneratedTemplateStructureIn(MAINTAINED_LIBRARY, raw.template)
      : await validateGeneratedTemplateAsyncIn(MAINTAINED_LIBRARY, raw.template);
    if (!checked.ok) throw new Error(`Composed template: ${checked.errors.slice(0, 3).join('; ')}`);
    return { kind, template: checked.template,
      ...(assembly ? { assembly } : {}) };
  }
  if (kind === 'adaptation') {
    const raw = record(value, ['kind', 'changes', 'integration'], 'composed adaptation');
    const changes = lines(raw.changes, 16, COMPOSE_PLAN_LIMITS.need, 'composed adaptation changes');
    if (!changes.length) throw new Error('Composed adaptation: no changes');
    const integration = validateStructure(raw.integration, moduleIds);
    if (!integration.ok) throw new Error(`Composed adaptation: ${integration.errors.slice(0, 3).join('; ')}`);
    return { kind, changes, integration: integration.structure };
  }
  if (kind !== 'rule') throw new Error('Composed step: kind must be base, template, adaptation or rule');
  const raw = record(value, ['kind', 'need', 'module', 'integration'], 'composed generated rule');
  const [module] = validateGeneratedComposeModulesV1([raw.module]);
  const need = parseComposeModuleNeedV1(raw.need, moduleIds);
  if (need.id !== module!.id) throw new Error('Composed generated rule: the module is not the rule it names');
  const integration = validateStructure(raw.integration, new Set([...moduleIds, module!.id]));
  if (!integration.ok) throw new Error(`Composed generated rule: ${integration.errors.slice(0, 3).join('; ')}`);
  return { kind, need, module: module!, integration: integration.structure };
}

/** Closed parser of a receipted step (wire). Shapes only: functions
 * authenticate the receipt over the exact step. */
export async function parseComposedStepV1(value: unknown, moduleIds: ReadonlySet<string>): Promise<ComposedStepV1> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid composed step');
  const { receipt, ...draft } = value as Record<string, unknown>;
  return { ...await parseComposedStepDraftV1(draft, moduleIds), receipt: parseComposedStepReceiptV1(receipt) } as ComposedStepV1;
}

/** Closed parser of a revision base: a positive version, the authority
 * digest and the accepted composed spec (validated, canonical). */
export function parseComposedRevisionBaseV1(value: unknown): ComposedRevisionBaseV1 {
  const raw = record(value, ['version', 'authorityDigest', 'spec'], 'composed revision base');
  if (!Number.isSafeInteger(raw.version) || (raw.version as number) < 1) throw new Error('Invalid composed revision base version');
  validateSha256(raw.authorityDigest, 'composed revision base authority');
  const spec = validateComposedGameSpecV1(raw.spec);
  if (canonicalJson(spec) !== canonicalJson(raw.spec)) throw new Error('Composed revision base spec is not in canonical form');
  return { version: raw.version as number, authorityDigest: raw.authorityDigest as string, spec };
}

/** Throws unless a step list is in Create order: at most one first step — a
 * revision's base, a generated template or an adaptation (so a revision never
 * also generates a template or adapts its base) — then rules; at most
 * {@link COMPOSED_STEPS_MAX} steps. */
export function composedStepOrderV1(steps: readonly Pick<ComposedStepV1, 'kind'>[]): void {
  if (steps.length > COMPOSED_STEPS_MAX) throw new Error(`a Create takes at most ${COMPOSED_STEPS_MAX} steps`);
  if (steps.some((s, i) => s.kind !== 'rule' && i > 0)) {
    throw new Error('the base, the generated template or the adaptation is a Create\'s first step, and only one of them');
  }
}

/** Its template id is a catalog template's, or the carried `generatedTemplate`'s. */
export function parseComposedProofReceiptV1(value: unknown, generatedTemplate: TemplateV1 | null): ComposedProofReceiptV1 {
  const raw = record(value, ['schemaVersion', 'templateId', 'templateDigest', 'specDigest', 'modulesDigest', 'proof', 'issuedAt', 'mac'],
    'composed proof receipt');
  if (raw.schemaVersion !== 1 || !Number.isSafeInteger(raw.issuedAt) || (raw.issuedAt as number) < 1) throw new Error('Invalid composed proof receipt');
  for (const key of ['templateDigest', 'specDigest', 'modulesDigest', 'mac'] as const) validateSha256(raw[key], `composed receipt ${key}`);
  return { schemaVersion: 1, templateId: selectionId(raw.templateId, generatedTemplate), templateDigest: raw.templateDigest as string,
    specDigest: raw.specDigest as string, modulesDigest: raw.modulesDigest as string, proof: parseComposedProofSummaryV1(raw.proof),
    issuedAt: raw.issuedAt as number, mac: raw.mac as string };
}

export function parseComposedSpecAcknowledgementV1(value: unknown): ComposedSpecAcknowledgementV1 {
  const carries = (key: string) => !!value && typeof value === 'object' && Object.hasOwn(value, key);
  const raw = record(value, ['schemaVersion', 'kind', 'templateId', 'templateDigest', 'patch', 'specDigest', 'receipt',
    'promptDigest', 'settingsDigest', 'designDigest', 'inputRevision', 'settingsRevision', 'designRevision', 'artDirection',
    ...(carries('modules') ? ['modules'] : []), ...(carries('generatedTemplate') ? ['generatedTemplate'] : [])], 'composed spec acknowledgement');
  if (raw.schemaVersion !== 1 || raw.kind !== 'composed-spec' || !raw.patch || typeof raw.patch !== 'object' || Array.isArray(raw.patch)
    || typeof raw.inputRevision !== 'string' || raw.inputRevision.length < 1 || raw.inputRevision.length > 300
    || raw.inputRevision.trim() !== raw.inputRevision
    || !Number.isSafeInteger(raw.settingsRevision) || (raw.settingsRevision as number) < 0
    || !Number.isSafeInteger(raw.designRevision) || (raw.designRevision as number) < 0
    || !raw.artDirection || typeof raw.artDirection !== 'object') {
    throw new Error('Invalid composed spec acknowledgement');
  }
  for (const key of ['templateDigest', 'specDigest', 'promptDigest', 'settingsDigest', 'designDigest'] as const) {
    validateSha256(raw[key], `composed acknowledgement ${key}`);
  }
  const generatedTemplate = carriedTemplate(raw);
  return {
    schemaVersion: 1, kind: 'composed-spec', templateId: selectionId(raw.templateId, generatedTemplate), templateDigest: raw.templateDigest as string,
    patch: structuredClone(raw.patch) as TemplateOverridesV1, specDigest: raw.specDigest as string,
    receipt: parseComposedProofReceiptV1(raw.receipt, generatedTemplate), promptDigest: raw.promptDigest as string,
    settingsDigest: raw.settingsDigest as string, designDigest: raw.designDigest as string,
    inputRevision: raw.inputRevision, settingsRevision: raw.settingsRevision as number, designRevision: raw.designRevision as number,
    artDirection: structuredClone(raw.artDirection) as CompositionArtDirectionV1,
    ...(carries('modules') ? { modules: validateGeneratedComposeModulesV1(raw.modules) } : {}),
    ...(generatedTemplate ? { generatedTemplate } : {}),
  };
}
